import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Form from '@cloudscape-design/components/form';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Select, { SelectProps } from '@cloudscape-design/components/select';
import Button from '@cloudscape-design/components/button';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Header from '@cloudscape-design/components/header';
import Container from '@cloudscape-design/components/container';
import Box from '@cloudscape-design/components/box';
import Flashbar, { FlashbarProps } from '@cloudscape-design/components/flashbar';
import Alert from '@cloudscape-design/components/alert';
import { addRule } from '../../api/rules';

const TARGET_TYPE_OPTIONS: SelectProps.Option[] = [
  { label: 'Lambda function', value: 'LAMBDA', description: 'Invoke a Lambda function with the event payload' },
  { label: 'SQS queue', value: 'SQS', description: 'Send event to an SQS queue for async processing' },
  { label: 'SNS topic', value: 'SNS', description: 'Publish event to an SNS topic for fan-out notifications' },
  { label: 'EventBridge bus', value: 'EVENTBRIDGE', description: 'Forward event to another EventBridge bus (cross-account)' },
  { label: 'Webhook (HTTPS)', value: 'WEBHOOK', description: 'POST event to an external HTTPS endpoint' },
  { label: 'Step Functions', value: 'STEP_FUNCTION', description: 'Start a Step Functions execution with the event' },
  { label: 'EventStore', value: 'EVENTSTORE', description: 'Forward event to another EventStore for cross-store lineage' },
];

const ARN_PATTERNS: Record<string, string> = {
  LAMBDA: 'arn:aws:lambda:us-east-2:123456789012:function:my-function',
  SQS: 'arn:aws:sqs:us-east-2:123456789012:my-queue',
  SNS: 'arn:aws:sns:us-east-2:123456789012:my-topic',
  EVENTBRIDGE: 'arn:aws:events:us-east-2:123456789012:event-bus/my-bus',
  WEBHOOK: 'https://api.example.com/webhooks/events',
  STEP_FUNCTION: 'arn:aws:states:us-east-2:123456789012:stateMachine:my-machine',
  EVENTSTORE: 'https://xxx.execute-api.us-east-2.amazonaws.com/v1',
};

interface AddRuleFormProps {
  storeName: string;
  onDone: () => void;
}

export function AddRuleForm({ storeName, onDone }: AddRuleFormProps) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [sourceEventType, setSourceEventType] = useState('');
  const [targetType, setTargetType] = useState<SelectProps.Option>(TARGET_TYPE_OPTIONS[0]);
  const [targetArn, setTargetArn] = useState('');
  const [targetEventType, setTargetEventType] = useState('');
  const [flash, setFlash] = useState<FlashbarProps.MessageDefinition[]>([]);
  const [errors, setErrors] = useState<{ name?: string; sourceEventType?: string; targetArn?: string }>({});

  const mutation = useMutation({
    mutationFn: () => addRule(
      storeName,
      name,
      sourceEventType,
      { type: targetType.value!, arn: targetArn },
      targetEventType.trim() || undefined
    ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rules', storeName] });
      onDone();
    },
    onError: (err: Error) => {
      setFlash([{ type: 'error', content: err.message, dismissible: true, onDismiss: () => setFlash([]) }]);
    },
  });

  const handleSubmit = () => {
    const newErrors: { name?: string; sourceEventType?: string; targetArn?: string } = {};
    if (!name.trim()) newErrors.name = 'Subscription name is required.';
    if (!sourceEventType.trim()) newErrors.sourceEventType = 'Source event type is required.';
    if (!targetArn.trim()) newErrors.targetArn = 'Target ARN is required.';
    setErrors(newErrors);
    if (Object.keys(newErrors).length === 0) mutation.mutate();
  };

  return (
    <SpaceBetween size="l">
      <Flashbar items={flash} />
      <Form
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDone}>Cancel</Button>
            <Button
              variant="primary"
              loading={mutation.isPending}
              onClick={handleSubmit}
            >
              Create subscription
            </Button>
          </SpaceBetween>
        }
      >
        <SpaceBetween size="l">
          <Container header={<Header variant="h2">Trigger</Header>}>
            <SpaceBetween size="m">
              <FormField
                label="Subscription name"
                description="Unique name for this subscription within the store."
                constraintText="Required"
                errorText={errors.name}
              >
                <Input
                  value={name}
                  onChange={({ detail }) => setName(detail.value)}
                  placeholder="process-payment"
                />
              </FormField>
              <FormField
                label="Source event type"
                description="The event type that triggers this subscription."
                constraintText="Required"
                errorText={errors.sourceEventType}
              >
                <Input
                  value={sourceEventType}
                  onChange={({ detail }) => setSourceEventType(detail.value)}
                  placeholder="OrderCreated"
                />
              </FormField>
            </SpaceBetween>
          </Container>

          <Container header={<Header variant="h2">Target</Header>}>
            <SpaceBetween size="m">
              <FormField
                label="Target type"
                description="The AWS service to deliver events to."
              >
                <Select
                  selectedOption={targetType}
                  onChange={({ detail }) => {
                    setTargetType(detail.selectedOption);
                    setTargetArn('');
                  }}
                  options={TARGET_TYPE_OPTIONS}
                />
              </FormField>
              <FormField
                label="Target ARN"
                description={`ARN of the ${targetType.label} to invoke when the source event fires.`}
                constraintText="Required"
                errorText={errors.targetArn}
              >
                <Input
                  value={targetArn}
                  onChange={({ detail }) => setTargetArn(detail.value)}
                  placeholder={ARN_PATTERNS[targetType.value!] || ''}
                />
              </FormField>
              <FormField
                label="Produced event type"
                description="If the target produces a new event back into the store, specify its type here. Enables lineage tracking."
              >
                <Input
                  value={targetEventType}
                  onChange={({ detail }) => setTargetEventType(detail.value)}
                  placeholder="PaymentProcessed"
                />
              </FormField>
            </SpaceBetween>
          </Container>

          <Alert type="info">
            When this subscription fires, the event router invokes your target with the full event payload.
            If a produced event type is specified, the target's response is written back as a new event
            with causal lineage linking it to the source.
          </Alert>
        </SpaceBetween>
      </Form>
    </SpaceBetween>
  );
}
